import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { PlanUsage, PlanInfo } from '../models/plan.model';

@Injectable({
  providedIn: 'root'
})
export class PlanService {
  private readonly apiUrl = `${environment.apiUrl}/plan`;
  private readonly paypalUrl = `${environment.apiUrl}/paypal`;
  private readonly subscriptionUrl = `${environment.apiUrl}/subscription`;

  private usageSubject = new BehaviorSubject<PlanUsage | null>(null);
  public usage$ = this.usageSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Get current user's plan usage statistics
   */
  getUsage(): Observable<PlanUsage> {
    return this.http.get<PlanUsage>(`${this.apiUrl}/usage`, { withCredentials: true })
      .pipe(
        tap(usage => this.usageSubject.next(usage))
      );
  }

  /**
   * Get all available plans information (public endpoint)
   */
  getPlans(): Observable<PlanInfo[]> {
    return this.http.get<PlanInfo[]>(`${this.apiUrl}/info`);
  }

  /**
   * Check if user can create a client
   */
  canCreateClient(): Observable<{ canCreate: boolean }> {
    return this.http.get<{ canCreate: boolean }>(`${this.apiUrl}/can-create-client`, { withCredentials: true });
  }

  /**
   * Check if user can create an invoice
   */
  canCreateInvoice(): Observable<{ canCreate: boolean }> {
    return this.http.get<{ canCreate: boolean }>(`${this.apiUrl}/can-create-invoice`, { withCredentials: true });
  }

  /**
   * Get cached usage (sync)
   */
  getCachedUsage(): PlanUsage | null {
    return this.usageSubject.getValue();
  }

  /**
   * Refresh usage data
   */
  refreshUsage(): void {
    this.getUsage().subscribe();
  }

  /**
   * Clear cached usage (on logout)
   */
  clearUsage(): void {
    this.usageSubject.next(null);
  }

  // ============ PayPal Integration ============

  /**
   * Get PayPal client ID for SDK
   */
  getPayPalConfig(): Observable<{ clientId: string }> {
    return this.http.get<{ clientId: string }>(`${this.paypalUrl}/config`);
  }

  /**
   * Create PayPal subscription and get approval URL
   */
  createPayPalSubscription(): Observable<{ subscriptionId: string; approvalUrl: string }> {
    return this.http.post<{ subscriptionId: string; approvalUrl: string }>(
      `${this.paypalUrl}/create-subscription`,
      {},
      { withCredentials: true }
    );
  }

  /**
   * Confirm PayPal subscription after approval
   */
  confirmPayPalSubscription(subscriptionId: string): Observable<{ success: boolean; message: string }> {
    return this.http.get<{ success: boolean; message: string }>(
      `${this.paypalUrl}/success?subscription_id=${subscriptionId}`,
      { withCredentials: true }
    );
  }

  /**
   * Get PayPal subscription status
   */
  getPayPalSubscriptionStatus(): Observable<{ isActive: boolean; status: string; nextBillingDate?: string }> {
    return this.http.get<{ isActive: boolean; status: string; nextBillingDate?: string }>(
      `${this.paypalUrl}/subscription-status`,
      { withCredentials: true }
    );
  }

  /**
   * Cancel PayPal subscription — enters grace period (CancellingAtPeriodEnd)
   */
  cancelPayPalSubscription(reason: string | null = null): Observable<{ message: string; currentPeriodEndsAt: string }> {
    return this.http.post<{ message: string; currentPeriodEndsAt: string }>(
      `${this.paypalUrl}/cancel-subscription`,
      { reason },
      { withCredentials: true }
    );
  }

  /**
   * Reactivate a subscription that is CancellingAtPeriodEnd
   */
  reactivateSubscription(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.subscriptionUrl}/reactivate`,
      {},
      { withCredentials: true }
    );
  }

  /**
   * Redirect to PayPal checkout
   */
  redirectToPayPal(): void {
    this.createPayPalSubscription().subscribe({
      next: (response) => {
        if (response.approvalUrl) {
          window.location.href = response.approvalUrl;
        }
      },
      error: (error) => {
        console.error('Failed to create PayPal subscription:', error);
      }
    });
  }
}
